/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BR_25_ANY_CONDITION
 * Date:         2026-02-06
 * Modified By:  Cursor
 *
 * Description:
 * BR-25: Added "Any Condition" option for WTB broadcasts only.
 * Filters condition options based on broadcast type - WTB shows all options
 * including "Any Condition", WTS hides "Any Condition".
 *
 * File Size Check:
 * - Current: 80 lines
 * - Adding: ~15 lines
 * - Projected: ~95 lines
 * - Decision: Proceed (within 200-line component limit)
 */
import React from "react";
import { useFormikContext, getIn } from "formik";
import { CONDITION_OPTIONS } from "../../../constants/inventoryOptions";

interface ConditionSelectProps {
  index: number;
  conditionNeedsReview?: boolean;
  onItemChange?: (index: number, field: string, value: any) => void;
}

const ConditionSelect: React.FC<ConditionSelectProps> = ({
  index,
  conditionNeedsReview,
  onItemChange,
}) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();

  const path = `broadcast.${index}.condition`;
  const err = getIn(errors, path);
  const tch = getIn(touched, path);
  const isInvalid = Boolean(tch && err);
  const describedBy = `${path}__error`;

  // [2026-02-06] BR-25: Get broadcast type to filter condition options
  const broadcastType = values.type as "WTB" | "WTS";
  const currentCondition = getIn(values, path) ?? "";

  // [2026-02-06] BR-25: Filter options - "Any Condition" only for WTB
  const filteredOptions = broadcastType === "WTB"
    ? CONDITION_OPTIONS
    : CONDITION_OPTIONS.filter((c) => c !== "Any Condition");

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(e);
    if (onItemChange) {
      onItemChange(index, "conditionNeedsReview", false);
    }
  };

  // [2026-02-06] BR-25: Reset condition if switching to WTS and "Any Condition" was selected
  React.useEffect(() => {
    if (broadcastType === "WTS" && currentCondition === "Any Condition") {
      setFieldValue(path, "");
    }
  }, [broadcastType, currentCondition, path, setFieldValue]);

  return (
    <div className="col-lg-3 col-md-6">
      <label htmlFor={`condition-${index}`} className="form-label">
        Condition *
        {conditionNeedsReview && (
          <span className="text-warning ms-1" title="Condition needs manual review">
            ⚠️
          </span>
        )}
      </label>
      <select
        id={`condition-${index}`}
        name={path}
        data-field-path={path}
        className={`form-select ${isInvalid ? "is-invalid" : ""} ${conditionNeedsReview ? "border-warning" : ""}`}
        value={currentCondition}
        onChange={handleConditionChange}
        onBlur={handleBlur}
        aria-invalid={isInvalid}
        aria-describedby={isInvalid ? describedBy : undefined}
      >
        <option value="">Select Condition</option>
        {filteredOptions.map((condition) => (
          <option key={condition} value={condition}>
            {condition}
          </option>
        ))}
      </select>
      {conditionNeedsReview && (
        <div className="text-warning small mt-1">
          ⚠️ Invalid condition from upload - please select a valid option
        </div>
      )}
      {isInvalid && (
        <div className="invalid-feedback" id={describedBy}>
          {err as string}
        </div>
      )}
    </div>
  );
};

export default ConditionSelect;

